import React, { useState } from 'react'
import { ButtonElement } from '@/react/components'
import closeIcon from '@/assets/icons/close-icon.svg'
import { css } from '@emotion/react'
import { useEffect, useRef } from 'react'

interface ConfirmationModalProps {
  handleClick: () => Promise<void>
  action?: string
  handleClose: () => void
  title: string
  questionMark?: boolean
}

export const ConfirmationModal = ({
  handleClick,
  action = '',
  handleClose,
  title = '',
  questionMark = true,
}: ConfirmationModalProps) => {
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [isButtonDisabled, setButtonDisabled] = useState(false)

  const handleOutsideClick = (e: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose()
    }
  }

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  useEffect(() => {
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  return (
    <div
      css={baseStyle}
      className="modal__wrapper"
      ref={modalRef}
      onMouseOver={(e) => {
        e.stopPropagation()
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <img
        src={closeIcon}
        alt="cross-icon"
        className="close__icon"
        onClick={() => {
          handleClose()
        }}
      />
      <div className="modal__info">
        <div
          className="modal__message"
          style={{ display: action ? 'block' : 'flex' }}
        >
          {action && (
            <strong>
              {truncateText(action, 20)}
              {questionMark ? '?' : ''}
            </strong>
          )}
          {action && <br />}
          <p className="message__name">{truncateText(title, 35)}</p>
        </div>
        <ButtonElement
          text="OK"
          buttonType="submit"
          functionToTrigger={() => {
            setButtonDisabled(true)
            handleClick()
            handleClose()
          }}
          disabled={isButtonDisabled}
        />
      </div>
    </div>
  )
}

const baseStyle = css({
  position: 'absolute',
  backgroundColor: 'white',
  boxShadow: '0px 7px 29px 0px rgba(0, 0, 0, 0.18)',
  marginTop: '-27px',
  right: 0,
  top: '28px',
  zIndex: 10000,
  '.modal__info': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 10px 15px 10px',
    width: '180px',
  },
  '.close__icon': {
    position: 'absolute',
    right: '4px',
    top: '5px',
    width: '13px',
    height: '13px',
    cursor: 'pointer',
  },
  '.modal__message': {
    color: '#666',
    lineHeight: 'normal',
    letterSpacing: '-0.14px',
    marginTop: '10px',
    marginBottom: '10px',
    textAlign: 'center',
    width: 'auto',
    fontWeight: 400,
    wordBreak: 'break-word',
    maxWidth: '160px',
  },
  '.message__name': {
    display: 'flex',
    justifyContent: 'center',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontSize: 14,
    width: '100%',
    textWrap: 'pretty',
    margin: 0,
    wordBreak: 'break-word',
  },
  '.delete__title': {
    marginBottom: '14px',
  },
  '.delete__subtitle': {
    color: '#666',
    textAlign: 'center',
    fontSize: '14px',
    margin: 0,
    marginBottom: '25px',
    width: '206px',
  },
  '.message__email': {
    margin: 0,
    fontWeight: 600,
  },
})
